import {
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Text,
} from "@chakra-ui/react";
import { useStytch } from "@stytch/react";
import {
  AuthFactorSetupProps,
  OtpAuthFactorInstance,
} from "features/auth/AuthFactor";
import { AuthHelpButton } from "features/auth/AuthHelpButton";
import { AuthFactorOtp } from "features/auth/factor/otp/AuthFactorOtp";
import { useStytchFormErrorHandler } from "features/auth/StytchUtils";
import { parsePhoneNumber } from "libphonenumber-js";
import { noop } from "lodash-es";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import { CardCloseButton } from "shared/components/card/CardCloseButton";
import Form from "shared/components/form/Form";
import { FormPatternInput } from "shared/components/input/text/PatternInput";

interface AuthOtpSetupPhoneFormData {
  phone_number: string;
}

export const AuthFactorOtpSetup: React.FC<AuthFactorSetupProps> = ({
  onCancel,
  onSuccess,
  rememberDevice,
}) => {
  const { t } = useTranslation(["common", "auth"]);
  const stytchClient = useStytch();
  const onStytchError = useStytchFormErrorHandler();

  const form = useForm<AuthOtpSetupPhoneFormData>();

  const [authFactor, setAuthFactor] = useState<OtpAuthFactorInstance>();

  if (authFactor) {
    return (
      <AuthFactorOtp
        instance={authFactor}
        onSuccess={onSuccess}
        onCancel={() => setAuthFactor(undefined)}
        onSelectAuthFactor={noop} // not used by AuthFactorOtp
        sendCode={false}
        rememberDevice={rememberDevice}
      />
    );
  }

  return (
    <Form
      form={form}
      onSubmit={(data: AuthOtpSetupPhoneFormData) => {
        const phone = parsePhoneNumber(data.phone_number, "US");
        return stytchClient.otps.sms.send(phone.number);
      }}
      onSuccess={(response, formData) => {
        setAuthFactor({
          id: response.method_id,
          type: "otp",
          data: {
            phone_id: response.method_id,
            phone_number: formData.phone_number,
          },
        });
      }}
      onError={onStytchError}
    >
      <Card width="md">
        <CardHeader>
          <Box>
            <Text textStyle="h4">{t("auth:authenticate")}</Text>
            <Text pt="24px">{t("auth:setup.otpInstructionsPhone")}</Text>
          </Box>
          <CardCloseButton onClick={onCancel} />
        </CardHeader>
        <CardBody pb={0}>
          <FormPatternInput
            name="phone_number"
            title={t("auth:phoneNumber")}
            format="(###) ###-####"
            autoFocus={true}
          />
        </CardBody>
        <CardFooter>
          <AuthHelpButton />
          <FormSubmitButton>{t("common:continue")}</FormSubmitButton>
        </CardFooter>
      </Card>
    </Form>
  );
};
